
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * Group
 *
 * A named set of firm users that can be collectively assigned tasks. Any group member can claim and work a task assigned to the group, enabling workload sharing without manual reassignment. Groups also carry an optional default billable rate.
 */
class Group extends Base implements EntityType {
    groupId: uuid;
    name: string;
    description?: string | null;
    billableRate?: number | null;
    userIds: uuid[];

    /**
     * Create a new Group, with an (optional) JSON object or Group class
     *
     * @param {Object|Group} doc - Can be either a simple JSON object or a Group class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Group | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            groupId: genUUID(),
            name: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'group';
        // @ts-ignore
        this.groupId = doc._groupId || doc.groupId || doc.groupid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.billableRate = util.coalesce(util.parseFloat(doc._billableRate), util.parseFloat(doc.billableRate), util.parseFloat(doc.billablerate), null);
        // @ts-ignore
        this.userIds = doc._userIds || doc.userIds || doc.userids || [];

    }
    /**
     * @returns {uuid} This is a generic pointer to the groupId across all entity types
     */
    get id(): uuid {
        return this.groupId;
    }


    /**
     * Add a userId to group.userIds
     *
     * @param {uuid} userId - The UserId to add
     * @returns uuid - The userId with any defaulted fields set
     */
    addUserId(userId: uuid) : uuid {
        if (!userId) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.userIds) this.userIds = [];
        this.userIds.push(userId);

        return userId;
    }

    /**
     * Update a userId from group
     *
     * @param {uuid} userId - The UserId to remove
     */
    updateUserId(userId: uuid) {
        if (!this.userIds) this.userIds = [];
        
        // Find the matching object by userId.userIdId
        const index = this.userIds.findIndex(item => item === userId);
        if (index !== -1) {
            this.userIds[index] = userId;
        }
        else this.addUserId(userId);
    }

    /**
     * Remove a userId from group
     *
     * @param {uuid} userId - The UserId to remove
     */
    removeUserId(userId: uuid) {
        if (!this.userIds) return;
        
        // Find the matching object by userId.userIdId
        let foundItems = this.userIds?.filter(item => item === userId);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.userIds = this.userIds.filter(item => !(item === userId));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonUserIds() {
        this.userIds = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for a group"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.groupId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["userIds"];
    }

    /**
     * Default entries are Outlaw-supplied Group entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Group[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'group',
            table: 'GROUPS',
            comment: 'A named set of firm users that can be collectively assigned tasks. Any group member can claim and work a task assigned to the group, enabling workload sharing without manual reassignment. Groups also carry an optional default billable rate.',
            fields: {
                groupId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique ID for a group'
                },
                name: {
                    type: 'string',
                    required: true,
                    comment: 'The name of the group'
                },
                description: {
                    type: 'string',
                    comment: 'Short description of the purpose of the group'
                },
                billableRate: {
                    type: 'money',
                    comment: 'Default billing rate for this group'
                },
                userIds: {
                    type: 'uuid[]',
                    entityType: 'user',
                    comment: 'User IDs of group members'
                }
            },
            maps: [{
                name: 'userIds',
                keysFn: t => t.userIds,
                presentFn: (t, k) => t.userIds?.includes(k)
            }]
        });
    }

}
export default Group;
